import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
import api from '../utils/api';
import CarModal from '../components/CarModal';

const Cars = () => {
  const { language, isRTL } = useLanguage();
  const t = translations[language];
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedCar, setSelectedCar] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchCars = async () => {
      try {
        const res = await api.get('/cars');
        setCars(res.data);
      } catch (error) {
        console.error('Error fetching cars:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCars();
  }, []);

  const carTypes = ['all', ...new Set(cars.map(car => car.type))];
  const filteredCars = filter === 'all' ? cars : cars.filter(car => car.type === filter);

  return (
    <div className="min-h-screen bg-blackC">
      {/* Hero Section */}
      <section className="relative pt-32 lg:pt-40">
        <div className="absolute inset-0 bg-gradient-to-b from-dark to-blackC" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <p className="section-subtitle animate-fade-in-up opacity-0 animation-delay-100">{t.carsSubtitle}</p>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-cream mb-6 animate-fade-in-up opacity-0 animation-delay-200">
              {t.viewCars}
            </h1>
            <div className="divider-gold mx-auto animate-fade-in-up opacity-0 animation-delay-300" />
          </div>
        </div>
      </section>

      {/* Filter & Cars */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            {carTypes.map((type) => (
              <button
                key={type}
                onClick={() => setFilter(type)}
                className={`px-6 py-2 text-sm tracking-wide uppercase transition-all duration-300 ${filter === type
                  ? 'bg-primary text-blackC'
                  : 'bg-transparent text-gray-silver border border-gray hover:border-primary hover:text-primary'
                  }`}
              >
                {type === 'all' ? (language === 'en' ? 'All Vehicles' : 'جميع السيارات') : type}
              </button>
            ))}
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredCars.map((car, index) => (
                <div
                  key={car._id}
                  className="group card-luxury cursor-pointer"
                  style={{ animationDelay: `${index * 100}ms` }}
                  onClick={() => {
                    setSelectedCar(car);
                    setCurrentImageIndex(0);
                  }}
                >
                  {/* Image */}
                  <div className="relative overflow-hidden h-72">
                    {car.images && car.images.length > 0 ? (
                      <img
                        src={car.images[0].url}
                        alt={car.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full bg-charcoal flex items-center justify-center">
                        <svg className="w-20 h-20 text-gray" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z" />
                        </svg>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-blackC via-transparent to-transparent opacity-80" />
                    <div className="absolute top-4 right-4 bg-primary text-blackC px-4 py-1.5 text-xs font-medium tracking-wider uppercase">
                      {car.type}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-8">
                    <h3 className="font-display text-3xl text-cream mb-6">{car.name}</h3>

                    {/* Specs */}
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="flex items-center gap-3 text-gray-silver">
                        <div className="w-10 h-10 rounded-full bg-charcoal flex items-center justify-center">
                          <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs text-gray-light uppercase tracking-wide">{t.passengers}</p>
                          <p className="text-cream">{car.passengerCapacity}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-gray-silver">
                        <div className="w-10 h-10 rounded-full bg-charcoal flex items-center justify-center">
                          <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs text-gray-light uppercase tracking-wide">{t.luggage}</p>
                          <p className="text-cream">{car.luggageCapacity} {t.bags}</p>
                        </div>
                      </div>
                    </div>

                    {/* Price & Book */}
                    <div className="flex items-center justify-between pt-6 border-t border-gray-dark">
                      <div>
                        {car.pricingType === 'fixed' && car.fixedPrice && (
                          <p className="text-primary font-display text-2xl">{car.fixedPrice} <span className="text-sm">EGP</span></p>
                        )}
                        {car.pricingType === 'perKm' && car.pricePerKm && (
                          <p className="text-primary font-display text-2xl">{car.pricePerKm} <span className="text-sm">EGP/km</span></p>
                        )}
                      </div>
                      <Link
                        to="/book"
                        className="btn-secondary py-2 px-6 text-xs"
                      >
                        {t.book}
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && filteredCars.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-light text-lg">
                {language === 'en' ? 'No vehicles available in this category.' : 'لا توجد سيارات متاحة في هذه الفئة.'}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-dark border-t border-primary/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl text-cream mb-6">
            {language === 'en' ? 'Need a Custom Quote?' : 'تحتاج عرض سعر مخصص؟'}
          </h2>
          <p className="text-gray-silver mb-8">
            {language === 'en'
              ? 'Contact us for special requests, group bookings, or custom routes.'
              : 'تواصل معنا للطلبات الخاصة والحجوزات الجماعية أو المسارات المخصصة.'}
          </p>
          <Link to="/contact" className="btn-primary">
            {t.contactUs}
          </Link>
        </div>
      </section>

      {/* Car Details Modal */}
      {selectedCar && (
        <CarModal car={selectedCar} onClose={() => setSelectedCar(null)} />
      )}
    </div>
  );
};

export default Cars;